import React, { useCallback, useEffect, useState } from "react";
import { Model, Firm } from "../types";
import { modelService } from "../services/dataService";
import { poErr } from "../utils/purchasing";
import { SinglePicker, PickColumn, trLower, dash } from "./PickerModal";
// Bu modal artık ModelModal.css sınıflarını kullanmıyor; ancak dosyadaki genel kurallar (.modal-overlay, .modal-content, .form-group,
// .form-row, .btn …) başka eski ekranlarda (Sipariş düzenle, maliyet modalları vb.) hâlâ bu içe aktarma sayesinde yükleniyor. Silmeyin.
import "./ModelModal.css";

interface ModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (model: Model) => void;
  firmId?: string;
  selectedFirm?: Firm | null;
}

const emptyModel = { modelCode: "", modelName: "", patternCode: "", description: "" };

const columns: PickColumn<Model>[] = [
  {
    head: "Model kodu",
    cell: (m) => (
      <>
        <span className="po-code">{m.modelCode}</span>
        {m.patternCode && <div className="po-sub po-only-sm">Desen: {m.patternCode}</div>}
      </>
    ),
  },
  { head: "Model adı", has: (m) => !!m.modelName && m.modelName !== m.modelCode, cell: (m) => (m.modelName && m.modelName !== m.modelCode ? <span className="po-name">{m.modelName}</span> : dash) },
  { head: "Desen kodu", className: "po-hide-sm", has: (m) => !!m.patternCode, cell: (m) => m.patternCode || dash },
  { head: "Kategori", className: "po-hide-md", has: (m) => !!m.category, cell: (m) => m.category || dash },
];

const match = (m: Model, q: string) =>
  trLower(m.modelCode).includes(q) || trLower(m.modelName).includes(q) || trLower(m.patternCode).includes(q) || trLower(m.category).includes(q);

const ModelPicker: React.FC<Omit<ModelModalProps, "isOpen">> = ({ onClose, onSelect, firmId, selectedFirm }) => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [newModel, setNewModel] = useState(emptyModel);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadModels = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await modelService.getAll();
      // Eğer firmId varsa sadece o firmaya ait modelleri göster
      setModels(firmId ? data.filter((model) => model.firmId === firmId) : data);
    } catch (err) {
      console.error("Modeller yüklenemedi:", err);
      setLoadError("Modeller yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => { loadModels(); }, [loadModels]);

  const handleAddModel = async () => {
    setSaving(true);
    setError("");
    try {
      const modelData: any = {
        modelCode: newModel.modelCode.trim(),
        // Model adı sorulmuyor; girilmemişse modelCode kullanılacak
        modelName: newModel.modelName || newModel.modelCode.trim(),
        patternCode: newModel.patternCode || undefined,
        description: newModel.description || undefined,
        firmId: firmId, // Otomatik seçili firma
      };
      const created = await modelService.create(modelData);
      setNewModel(emptyModel);
      onSelect(created);
      onClose();
    } catch (err: any) {
      setError("Model eklenemedi: " + poErr(err, err?.message || "bilinmeyen hata"));
      setSaving(false);
    }
  };

  const set = (k: keyof typeof emptyModel, v: string) => { setNewModel((p) => ({ ...p, [k]: v })); setError(""); };
  const firmLabel = selectedFirm ? `${selectedFirm.firmName}${selectedFirm.firmCode ? ` (${selectedFirm.firmCode})` : ""}` : "";

  return (
    <SinglePicker<Model>
      kicker={selectedFirm ? `Model · ${selectedFirm.firmName}` : "Model"}
      title="Model seç"
      noun="model"
      items={models}
      loading={loading}
      loadError={loadError}
      onRetry={loadModels}
      rowKey={(m) => m.modelId}
      rowLabel={(m) => m.modelCode || m.modelName}
      match={match}
      placeholder="Model kodu, adı, desen veya kategori ara"
      columns={columns}
      notice={selectedFirm ? <div className="po-note info">Yalnız <b>{firmLabel}</b> firmasının modelleri listelenir.</div> : undefined}
      emptyText={selectedFirm ? "Bu firmaya ait model yok; ilk modeli buradan ekleyebilirsiniz." : "İlk modeli buradan ekleyebilirsiniz."}
      onPick={(m) => { onSelect(m); onClose(); }}
      onClose={onClose}
      create={{
        label: "Yeni model",
        title: "Yeni model",
        hint: selectedFirm ? <>Model <b>{firmLabel}</b> firmasına otomatik atanır; adı girilmezse model kodu kullanılır.</> : "Model adı girilmezse model kodu kullanılır.",
        onOpen: (q) => { if (q && !newModel.modelCode) set("modelCode", q); },
        onSubmit: handleAddModel,
        saving,
        error,
        valid: !!newModel.modelCode.trim(),
        fields: (
          <>
            <div className="po-grid2">
              <label className="po-field">
                <span>Model kodu<em>zorunlu</em></span>
                <input value={newModel.modelCode} maxLength={100} required onChange={(e) => set("modelCode", e.target.value)} placeholder="Örn. TK-700" />
              </label>
              <label className="po-field">
                <span>Desen kodu<em>isteğe bağlı</em></span>
                <input value={newModel.patternCode} maxLength={100} onChange={(e) => set("patternCode", e.target.value)} placeholder="Desen kodu" />
              </label>
            </div>
            <label className="po-field">
              <span>Açıklama<em>isteğe bağlı</em></span>
              <textarea rows={3} value={newModel.description} onChange={(e) => set("description", e.target.value)} />
            </label>
          </>
        ),
      }}
    />
  );
};

/** Model seçici: firmaya göre süzülmüş modeller, arama, klavye ile seçim ve satır içi yeni model formu. */
const ModelModal: React.FC<ModelModalProps> = ({ isOpen, ...rest }) => (isOpen ? <ModelPicker {...rest} /> : null);

export default ModelModal;
